import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, CheckCircle, XCircle, Phone } from 'lucide-react';
import axios from 'axios';
import { submitLead } from '../../services/hubspot';
import PrivacyCheckbox from '../../components/PrivacyCheckbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  'Professionelle Mietersuche',
  'Bonitätsprüfung aller Interessenten',
  'Rechtssichere Mietverträge',
  'Begleitung bei der Übergabe',
  'Erstellung von Exposés',
  'Optional: Laufende Betreuung'
];

export default function Vermietung() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    message: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) return;
    
    setLoading(true);
    setStatus(null);

    const hubspotResult = await submitLead('vermietung', {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      propertyType: formData.property_type,
      message: formData.message
    });

    try {
      await axios.post(`${API}/api/lead`, { ...formData, service_type: 'Vermietung' });
    } catch (error) {
      console.error('Backend submission error:', error);
    }

    setLoading(false);
    
    if (hubspotResult.success) {
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', property_type: '', message: '' });
      setPrivacyAccepted(false);
    } else {
      setStatus('error');
    }
  };

  const StatusMessage = () => (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`p-4 mb-6 flex items-start gap-3 ${
            status === 'success' 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-red-500/10 border border-red-500/30'
          }`}
        >
          {status === 'success' ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-400 font-medium">Vielen Dank!</p>
                <p className="text-green-400/80 text-sm">Serdar Dogan meldet sich in Kürze bei Ihnen.</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Es ist ein Fehler aufgetreten.</p>
                <p className="text-red-400/80 text-sm">
                  Bitte rufen Sie uns direkt an: 
                  <a href="tel:+491756900374" className="ml-1 inline-flex items-center gap-1 text-[#d1af6a] hover:underline">
                    <Phone className="w-3 h-3" /> +49 175 6900374
                  </a>
                </p>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div data-testid="vermietung-page">
      <section className="relative pt-32 pb-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <Link to="/leistungen" className="text-[#c8c6c4] hover:text-[#d1af6a]">Leistungen</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">Vermietung</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">VERMIETUNG</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">Professionelle Vermietung</h1>
            <p className="text-[#c8c6c4] text-lg leading-relaxed">
              Finden Sie den passenden Mieter für Ihre Immobilie – mit professioneller Mieterprüfung, rechtssicheren Verträgen und persönlicher Betreuung.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl text-white mb-6">Stressfreie Vermietung</h2>
              <p className="text-[#c8c6c4] leading-relaxed mb-8">
                Die Auswahl des richtigen Mieters ist entscheidend für eine langfristig erfolgreiche Vermietung. Ich übernehme die gesamte Abwicklung – von der Mietersuche bis zur Schlüsselübergabe.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3 text-white">
                    <Check className="w-5 h-5 text-[#d1af6a] flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-luxury p-8">
              <h3 className="font-subheading text-xl text-white mb-6">Der Vermietungsprozess</h3>
              <ol className="space-y-6">
                {['Bewertung & Mietpreisermittlung', 'Erstellung eines ansprechenden Exposés', 'Vermarktung & Interessentenauswahl', 'Besichtigungen & Bonitätsprüfung', 'Mietvertragsgestaltung', 'Übergabe & Dokumentation'].map((step, index) => (
                  <li key={step} className="flex gap-4">
                    <span className="text-[#d1af6a] font-display text-lg">{String(index + 1).padStart(2, '0')}</span>
                    <span className="text-[#c8c6c4] text-sm pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#1a2640]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">Ich möchte meine Immobilie vermieten</h2>
            <p className="text-[#c8c6c4]">Kontaktieren Sie mich für ein unverbindliches Gespräch.</p>
          </div>
          
          <StatusMessage />
          
          {status !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="vermietung-form">
              <input type="text" placeholder="Ihr Name *" required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="input-luxury w-full" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="email" placeholder="E-Mail *" required value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} className="input-luxury w-full" />
                <input type="tel" placeholder="Telefon *" required value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="input-luxury w-full" />
              </div>
              <Select value={formData.property_type} onValueChange={(value) => setFormData(prev => ({ ...prev, property_type: value }))}>
                <SelectTrigger className="input-luxury w-full h-auto"><SelectValue placeholder="Art der Immobilie" /></SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Wohnung">Wohnung</SelectItem>
                  <SelectItem value="Haus">Haus</SelectItem>
                  <SelectItem value="Gewerbe">Gewerbe</SelectItem>
                </SelectContent>
              </Select>
              <textarea placeholder="Ihre Nachricht (optional)" rows={3} value={formData.message} onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))} className="input-luxury w-full resize-none" />
              <PrivacyCheckbox checked={privacyAccepted} onCheckedChange={setPrivacyAccepted} />
              <button type="submit" disabled={loading || !privacyAccepted} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Wird gesendet…' : 'Anfrage senden'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
